import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Accordion,
  Card,
  EmptyState,
  FilterSelect,
  Input,
  PageBody,
  Spinner,
  TablePagination,
  type FilterOption,
} from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import { UserPicker } from '@/components/pickers/UserPicker.tsx';
import { ChangeLogCard } from '@/components/admin/change-logs/ChangeLogCard.tsx';
import {
  MultiSelectMenu,
  type MultiSelectOption,
} from '@/components/admin/change-logs/MultiSelectMenu.tsx';
import { listChangeLogs, type ChangeLog } from '@/lib/changeLogs.ts';
import { getUser } from '@/lib/users.ts';

/** The filters, as they live in the URL — so a change log can be linked to. */
export interface ChangeLogsSearch {
  /** Comma-joined entity types. Absent is "all of them". */
  entity_type: string | undefined;
  action: string | undefined;
  entity_id: string | undefined;
  user_id: string | undefined;
  limit: number | undefined;
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

export const Route = createFileRoute('/_app/admin/change-logs')({
  staticData: { orgScope: 'none' },
  validateSearch: (search: Record<string, unknown>): ChangeLogsSearch => ({
    entity_type: text(search.entity_type),
    action: text(search.action),
    entity_id: text(search.entity_id),
    user_id: text(search.user_id),
    limit: Number(search.limit) > 0 ? Number(search.limit) : undefined,
  }),
  component: ChangeLogsRoute,
});

function ChangeLogsRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <ChangeLogsPage
      search={search}
      onSearchChange={(next) => void navigate({ search: next })}
    />
  );
}

const ENTITY_TYPE_OPTIONS: MultiSelectOption[] = [
  { value: 'skill', label: 'Skill' },
  { value: 'track', label: 'Track' },
  { value: 'user', label: 'User' },
  { value: 'organisation', label: 'Organisation' },
  { value: 'cohort', label: 'Cohort' },
  { value: 'memory', label: 'Memory' },
  { value: 'trackParticipant', label: 'Track Participant' },
  { value: 'actionPoint', label: 'Action Point' },
  { value: 'document', label: 'Document' },
];

const ACTION_OPTIONS: MultiSelectOption[] = [
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
];

const PAGE_SIZE_OPTIONS: FilterOption<string>[] = [10, 25, 50, 100, 200].map((size) => ({
  value: String(size),
  label: `${size} per page`,
}));

const DEFAULT_PAGE_SIZE = 25;

const ENTITY_TYPE_ROUTES: Record<string, string> = {
  skill: '/skills',
  track: '/tracks',
  user: '/users',
  organisation: '/organisations',
  cohort: '/cohorts',
};

/* Things without a page of their own, which live on a tab of the user's. */
const USER_PAGE_ENTITIES: Record<string, string> = {
  memory: '#memories',
  actionPoint: '#action-points',
  trackParticipant: '#tracks',
};

function entityHrefFor(log: ChangeLog): string {
  const basePath = ENTITY_TYPE_ROUTES[log.entity_type];
  if (basePath) return `${basePath}/${log.entity_id}`;

  const hash = USER_PAGE_ENTITIES[log.entity_type];
  if (hash) {
    const entityUserId = log.data?.user_id;
    if (typeof entityUserId === 'string') return `/users/${entityUserId}${hash}`;
  }
  return '';
}

function split(value: string | undefined): string[] {
  return value ? value.split(',') : [];
}

export interface ChangeLogsPageProps {
  search: ChangeLogsSearch;
  onSearchChange: (search: ChangeLogsSearch) => void;
}

/**
 * Every recorded edit, newest first.
 *
 * The filters live in the URL rather than in the page: a change log is
 * something people link each other to — "look at what happened to this cohort"
 * — and a filtered view that cannot be sent is half a tool.
 */
export function ChangeLogsPage({ search, onSearchChange }: ChangeLogsPageProps) {
  const [page, setPage] = useState(1);
  const [entityId, setEntityId] = useState(search.entity_id ?? '');
  const [expanded, setExpanded] = useState<number[]>([]);

  const pageSize = search.limit ?? DEFAULT_PAGE_SIZE;

  /* Every filter narrows the whole list, so a narrowed list starts at page one. */
  function update(next: Partial<ChangeLogsSearch>) {
    setPage(1);
    onSearchChange({ ...search, ...next });
  }

  const params = {
    page,
    limit: pageSize,
    entityTypes: split(search.entity_type),
    actions: split(search.action),
    entityId: search.entity_id,
    userId: search.user_id,
  };

  const { data, isPending } = useQuery({
    queryKey: ['changeLogs', params],
    queryFn: () => listChangeLogs(params),
    placeholderData: keepPreviousData,
  });

  /* The URL carries an id; the picker shows a person, so the id is looked up. */
  const { data: filterUser } = useQuery({
    queryKey: ['user', search.user_id],
    queryFn: () => getUser(search.user_id!),
    enabled: Boolean(search.user_id),
  });

  const logs = data?.data ?? [];

  return (
    <>
      <PageHeader title='Change Logs' />

      <PageBody className='flex flex-col gap-5'>
        <Card tone='quiet' className='flex flex-wrap items-center gap-3.5'>
          <MultiSelectMenu
            label='Entity'
            plural='entities'
            options={ENTITY_TYPE_OPTIONS}
            value={split(search.entity_type)}
            onChange={(values) =>
              update({ entity_type: values.length > 0 ? values.join(',') : undefined })
            }
          />

          <MultiSelectMenu
            label='Action'
            plural='actions'
            options={ACTION_OPTIONS}
            value={split(search.action)}
            onChange={(values) =>
              update({ action: values.length > 0 ? values.join(',') : undefined })
            }
          />

          <Input
            aria-label='Entity ID'
            placeholder='Entity ID'
            className='w-56'
            value={entityId}
            onChange={(event) => setEntityId(event.target.value)}
            onBlur={() => update({ entity_id: entityId || undefined })}
            onKeyDown={(event) => {
              if (event.key === 'Enter') update({ entity_id: entityId || undefined });
            }}
          />

          <div className='w-64'>
            <UserPicker
              aria-label='User'
              placeholder='Filter by user...'
              value={filterUser ?? null}
              onChange={(user) => update({ user_id: user?.id })}
            />
          </div>

          <FilterSelect
            hideLabel
            className='ml-auto'
            label='Per page'
            options={PAGE_SIZE_OPTIONS}
            value={String(pageSize)}
            onValueChange={(value) =>
              update({
                limit: Number(value) === DEFAULT_PAGE_SIZE ? undefined : Number(value),
              })
            }
          />
        </Card>

        {isPending && <Spinner className='size-5 text-ink-muted' />}

        {!isPending && logs.length === 0 && (
          <EmptyState title='No change logs match the current filters.' />
        )}

        {logs.length > 0 && (
          <Accordion
            className='gap-2'
            value={expanded}
            onValueChange={(value) => setExpanded(value as number[])}
          >
            {logs.map((log) => (
              <ChangeLogCard
                key={log.id}
                log={log}
                entityHref={entityHrefFor(log)}
                expanded={expanded.includes(log.id)}
              />
            ))}
          </Accordion>
        )}

        {logs.length > 0 && (
          <TablePagination
            page={page}
            pageSize={pageSize}
            total={data?.meta.totalItems ?? 0}
            onPageChange={setPage}
          />
        )}
      </PageBody>
    </>
  );
}
